import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const ContactInfo = () => {
  const contactMethods = [
    {
      id: 1,
      icon: "MapPin",
      title: "Office Location",
      details: ["8225 Shooting Star Lane, Laurel, MD 20724"],
    
      link: null
    },
    {
      id: 2,
      icon: "Phone",
      title: "Phone Numbers",
      details: ["+1 240-598-5442", "+254 707 366350", "+231 770066992 WhatsApp Only"],
      link: "tel: +254 707 366350"
    },
    {
      id: 3,
      icon: "Mail",
      title: "Email Addresses",
      details: ["info@daraja.com", "support@daraja.com"],
      link: "mailto:info@daraja.labs"
    },
    {
      id: 4,
      icon: "Clock",
      title: "Business Hours",
      details: ["Monday - Friday: 9:00 AM - 6:00 PM", "Saturday: 10:00 AM - 4:00 PM", "Sunday: Closed"],
      link: null
    }
  ];

  const socialPlatforms = [
    { name: "LinkedIn", icon: "Linkedin", url: "https://linkedin.com/company/daraja-labs" },
    { name: "Twitter", icon: "Twitter", url: "https://twitter.com/darajalabs" },
    { name: "GitHub", icon: "Github", url: "https://github.com/daraja-labs" },
    { name: "Instagram", icon: "Instagram", url: "https://instagram.com/darajalabs" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
          Get in Touch
        </h2>
        <p className="text-text-secondary text-lg leading-relaxed">
          Ready to bridge the gap to your digital future? Our team is here to help you transform your business with modern IT solutions.
        </p>
      </motion.div>
      {/* Contact Methods */}
      <motion.div variants={itemVariants} className="space-y-6">
        {contactMethods?.map((method) => (
          <motion.div
            key={method?.id}
            className="group p-6 bg-card rounded-xl border border-border hover:border-accent/50 transition-all duration-300 hover-scale"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center group-hover:bg-accent/20 transition-colors duration-300">
                <Icon 
                  name={method?.icon} 
                  size={24} 
                  className="text-accent group-hover:scale-110 transition-transform duration-300" 
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-card-foreground mb-2">
                  {method?.title}
                </h3>
                <div className="space-y-1">
                  {method?.details?.map((detail, index) => (
                    <p key={index} className="text-muted-foreground">
                      {method?.link && index === 0 ? (
                        <a
                          href={method?.link}
                          className="hover:text-accent transition-colors duration-200 focus-ring rounded"
                        >
                          {detail}
                        </a>
                      ) : (
                        detail
                      )}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
      {/* Map Integration */}
      <motion.div variants={itemVariants} className="rounded-xl overflow-hidden border border-border">
        <div className="h-64 bg-muted relative">
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="DARAJA.LABS Office Location"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=37.4419,-122.1430&z=14&output=embed"
            className="absolute inset-0"
          />
        </div>
      </motion.div>
      {/* Social Media */}
      <motion.div variants={itemVariants}>
        <h3 className="text-xl font-semibold text-foreground mb-4">
          Connect With Us
        </h3>
        <div className="flex space-x-4">
          {socialPlatforms?.map((platform) => (
            <motion.a
              key={platform?.name}
              href={platform?.url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-card border border-border rounded-lg flex items-center justify-center hover:border-accent/50 hover:bg-accent/10 transition-all duration-300 focus-ring"
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
              aria-label={`Follow us on ${platform?.name}`}
            >
              <Icon 
                name={platform?.icon} 
                size={20} 
                className="text-muted-foreground hover:text-accent transition-colors duration-300" 
              />
            </motion.a>
          ))}
        </div>
      </motion.div>
      {/* Response Time Indicator */}
      <motion.div 
        variants={itemVariants}
        className="p-4 bg-accent/10 rounded-lg border border-accent/20"
      >
        <div className="flex items-center space-x-3">
          <Icon name="Clock" size={20} className="text-accent" />
          <div>
            <p className="text-sm font-medium text-foreground">
              Average Response Time
            </p>
            <p className="text-xs text-muted-foreground">
              We typically respond within 2-4 business hours
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ContactInfo;